import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { buildGenericFallbackImageData } from '@susu/custom-made/utils/fallbackImage';
import type { SyntheticEvent } from 'react';
import { useMemo, useState } from 'react';

// Swaps a broken <img> to an item-specific fallback first (if one is given) and only
// drops to the generic fallback we know exists if that also fails. Callers key state by
// whatever identifies the image (its original src works well) and read
// `genericFallbackImages[key]` to style that specific image differently once it's on the
// generic fallback, without affecting any other image sharing the same hook instance.
export const useImageFallback = () => {
  const { cdnDomain } = useEnvironmentContext();
  const [genericFallbackImages, setGenericFallbackImages] = useState<Record<string, boolean>>({});

  const genericFallbackImageUrl = useMemo(() => buildGenericFallbackImageData(cdnDomain).src, [cdnDomain]);

  const handleImageError = (
    e: SyntheticEvent<HTMLImageElement, Event>,
    key: string,
    itemFallbackUrl?: string,
  ) => {
    // Generic fallback is terminal: never swap away from it.
    if (e.currentTarget.src === genericFallbackImageUrl) {
      setGenericFallbackImages(prev => (prev[key] ? prev : { ...prev, [key]: true }));
      return;
    }

    if (itemFallbackUrl && e.currentTarget.src !== itemFallbackUrl) {
      e.currentTarget.src = itemFallbackUrl;
      return;
    }

    e.currentTarget.src = genericFallbackImageUrl;
    setGenericFallbackImages(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  // Also fires when the swapped-in fallback finishes loading, so only clear the flag
  // when the *original* src is the one that ends up loading successfully.
  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement, Event>, key: string) => {
    if (e.currentTarget.src === genericFallbackImageUrl) {
      return;
    }

    setGenericFallbackImages(prev => {
      if (!prev[key]) {
        return prev;
      }
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  return { genericFallbackImages, handleImageError, handleImageLoad };
};
